import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import '../../common/CommonText.css'
import './UserDropdown.css';
import useFilterStore from "../../../store/useFilterStore";
import { useAuth } from "../../../contexts/AuthContext";
import { baseURL } from "../../../services/api";
import { GetMyDetails } from "../../../services/MyProfileApi";
import LogoutConfirmationPopup from "../LogoutConfirmation/LogoutConfirmationPopup";
import { useTranslation } from "react-i18next";

import useUserStore from "../../../store/useUserStore";
import TruncatedText from "../../common/TruncatedText/TruncatedText";

const UserDropdown = ({ isOpen, onClose, isRestricted }) => {
    const popupRef = useRef(null);
    const navigate = useNavigate();
    const { handleLogout } = useAuth();
    const { t } = useTranslation();
    const userStoreData = useUserStore((state) => state.user);
    const setUserStore = useUserStore((state) => state.setUser);
    const [localUserData, setLocalUserData] = useState(null);
    const [showLogoutPopup, setShowLogoutPopup] = useState(false);

    const userData = userStoreData || localUserData;

    const profile_url = userData?.profile_pic
      ? userData.profile_pic.startsWith("http")
        ? userData.profile_pic
        : `${baseURL.slice(0, -1)}/${userData.profile_pic.replace(/^\//, "")}`
      : null;
  
    useEffect(() => {
      fetchUserDetails();
    }, []);

    useEffect(() => {
      if (isOpen) {
        fetchUserDetails();
      }
    }, [isOpen]);
    
    async function fetchUserDetails() {
      try {
        const response = await GetMyDetails();
        if (response) {
          setLocalUserData(response);
          if (response?.profile_pic && !response.profile_pic.startsWith("http")) {
            response.profile_pic = `${baseURL.slice(0, -1)}/${response.profile_pic.replace(/^\//, "")}`;
          }
          setUserStore(response);
        }
      } catch (err) {
        console.error("Error fetching my details:", err);
      }
    }

    const handleLogoutClick = () => {
        setShowLogoutPopup(true);
    };

    const confirmLogout = () => {
        handleLogout();
        setShowLogoutPopup(false);
        onClose();
        useFilterStore.getState().clearAllFilters();
    };

    const cancelLogout = () => {
        setShowLogoutPopup(false);
    };

    useEffect(() => {

            if (showLogoutPopup) return;

            const handleClickOutside = (event) => {
                if (popupRef.current && !popupRef.current.contains(event.target)) {
                    onClose(); 
                }
            };
            document.addEventListener("mousedown", handleClickOutside);

            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }, [onClose, showLogoutPopup]);
    if (!isOpen) return null;

    const handlePersonalDetailClick = () => {
        navigate(`/my-profile`, { state: { activeTab: 'profile', openPlanTab: false } }); 
        onClose();
    };
    
    const handleMyOrgClick = () =>{
        navigate('/my-organization', { state: { activeTab: 'Overview' } });
        onClose();
    }

    return (
        <>
            <div className="my-profile-popup-overlay" >
                <div className="myprofile-popup-container " ref={popupRef}>
                    <div className='myprofile-popup'>
                        <div className="user-info user-dpdn">
                            <img
                                src={profile_url || "/assets/no-profile.png"}
                                alt="user profile"
                                className="user-image"
                            />
                            <div className="username-container">
                                <span className="user-name common-text ftw-4 black">
                                    <TruncatedText text={userData?.full_name} limit={15} />
                                </span>
                                <span className="role common-text ftw-4 dk-gray">
                                    <TruncatedText text={typeof userData?.role === 'object' ? userData?.role?.name : (userData?.role || userData?.role_name)} limit={15} />
                                </span>
                            </div>
                        </div>
                        {true && (
                            <>
                                <button className='my-profile-option' onClick={handlePersonalDetailClick}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M13.3333 14V12.6667C13.3333 11.9594 13.0523 11.2811 12.5522 10.781C12.0521 10.281 11.3739 10 10.6666 10H5.33329C4.62605 10 3.94777 10.281 3.44767 10.781C2.94758 11.2811 2.66663 11.9594 2.66663 12.6667V14M10.6666 4.66667C10.6666 6.13943 9.47272 7.33333 7.99996 7.33333C6.5272 7.33333 5.33329 6.13943 5.33329 4.66667C5.33329 3.19391 6.5272 2 7.99996 2C9.47272 2 10.6666 3.19391 10.6666 4.66667Z" stroke="#454545" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                    <span className='common-text dk-gray ftw-4'>{t('My Profile')}</span>
                                </button>
                                <button className='my-profile-option my-profile-option-logout' onClick={handleMyOrgClick}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <g clip-path="url(#clip0_12473_65776)">
                                        <path d="M8 4V6.66667" stroke="#454545" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M9.33329 9.33333H6.66663" stroke="#454545" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M9.33329 12H6.66663" stroke="#454545" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M9.33329 5.33333H6.66663" stroke="#454545" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M12 8H13.3334C13.687 8 14.0261 8.14048 14.2762 8.39052C14.5262 8.64057 14.6667 8.97971 14.6667 9.33333V13.3333C14.6667 13.687 14.5262 14.0261 14.2762 14.2761C14.0261 14.5262 13.687 14.6667 13.3334 14.6667H2.66671C2.31309 14.6667 1.97395 14.5262 1.7239 14.2761C1.47385 14.0261 1.33337 13.687 1.33337 13.3333V7.33333C1.33337 6.97971 1.47385 6.64057 1.7239 6.39052C1.97395 6.14048 2.31309 6 2.66671 6H4.00004" stroke="#454545" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M12 14.6667V2.66666C12 2.31304 11.8595 1.9739 11.6095 1.72385C11.3594 1.4738 11.0203 1.33333 10.6667 1.33333H5.33333C4.97971 1.33333 4.64057 1.4738 4.39052 1.72385C4.14048 1.9739 4 2.31304 4 2.66666V14.6667" stroke="#454545" stroke-linecap="round" stroke-linejoin="round"/>
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_12473_65776">
                                        <rect width="16" height="16" fill="white"/>
                                        </clipPath>
                                    </defs>
                                    </svg>
                                    <span className='common-text dk-gray ftw-4'>{t('My Organization')}</span>
                                </button>
                            </>
                        )}
                        <button className='my-profile-option my-profile-option-logout' onClick={handleLogoutClick}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M6 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H6M10.6667 4.66667L14 8L10.6667 11.3333M14 8H6" stroke="#454545" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            <span className='common-text dk-gray ftw-4'>{t('Log Out')}</span>
                        </button>       
                    </div>
                </div>
            </div>

            <LogoutConfirmationPopup
                isOpen={showLogoutPopup}
                onConfirm={confirmLogout}
                onClose={cancelLogout}
            />
        </>
    );
};

UserDropdown.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default UserDropdown;